import { Box, Stack } from '@mui/material';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as poiState from '../../../../state';
import * as analysisState from '@/components/Analysis/state';
import {
  PaintDamageCountByAlarmLevelDocument,
  PointOfInterestsCountByDueDateDocument,
  PointOfInterestsCountByInspectionPriorityDocument,
  PoiStatus,
  useAllPaintRegionsForFilterAndSearchPanelQuery,
  useFindClosestPaintRegionLazyQuery,
  PoiAccessibilityEnum,
  useUpdatePoiGeneralFieldsMutation,
  useGetAllTeamsForCreateOrEditPoiQuery,
  AllPointOfInterestsForDataLoaderDocument,
  SelectedPointOfInterestDocument,
  PointOfInterestsCountByStatusDocument,
  CuboidInput,
} from '@/__generated__/graphql';
import { useGeneralFields, useTriggerPoiEditing } from '../../hooks';
import { GeneralFieldsForm } from '../GeneralFieldsForm';
import { StyledForm } from './styles';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { convertTimeStampToDate, upperSnakeCase } from '@/utils';
import { checkIfVoi } from '@/utils/poi';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as cuboidsState from '../../../../../cuboids/state';
import { useAnalysisCuboidActions } from '../../../../../cuboids/hooks';
import { AnalysisCuboid } from '@/types';
import { PoiFormSubHeading } from '../styles';
import { allVoiTemplates as allVoiTemplatesState } from '@/components/Analysis/state';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useRouter } from '@/hooks';

export const UpdateGeneralFields = () => {
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const setPoiFilteredByCheckbox = useSetAtom(poiState.poiFilteredByCheckbox);
  const currentCuboid = useAtomValue(cuboidsState.currentCuboid);
  const isCuboidEditorEnabled = useAtomValue(cuboidsState.isCuboidEditorEnabled);
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);
  const currentSpherical = useAtomValue(analysisState.viewerCurrentSpherical);

  const { pointOfInterest: poi } = editPointOfInterest || {};
  const [loading, setLoading] = useState(false);
  const navbarHeight = useNavbarHeight();
  const { checkByType } = useIsPoiWorkScope();

  const router = useRouter();
  const isAtViewerPage = useMemo(() => router.pathname.includes('viewer'), [router.pathname]);

  // Get structure ID using the dedicated hook
  const structureId = useGetStructureId();
  // Use the initial POI data

  // Get paint regions using the structureId from the hook
  const {
    data: paintRegionsData,
    loading: paintRegionsLoading,
    error: paintRegionsError,
  } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: {
      input: {
        structureId: structureId || '',
      },
    },
    fetchPolicy: 'cache-first',
    skip: !structureId, // Skip if structureId is not available
    onError: (error) => {
      console.warn('Error fetching paint regions, will continue without them:', error);
      // Continue without paint regions rather than showing an error
    },
  });

  // Set up paint region state and handlers - same as in CreatePoi
  const [selectedPaintRegionId, setSelectedPaintRegionId] = useState<string>('');

  // Handle paint region selection - same as in CreatePoi
  const handlePaintRegionChange = useCallback(
    (paintRegionName: string) => {
      const paintRegion = paintRegionsData?.allPaintRegions?.find(
        (region) => region.name === paintRegionName
      );
      if (paintRegion) {
        setSelectedPaintRegionId(paintRegion.id);
      }
    },
    [paintRegionsData?.allPaintRegions]
  );

  // Generate paint region options for the dropdown - same as in CreatePoi
  const paintRegionOptions = useMemo(() => {
    const options =
      paintRegionsData?.allPaintRegions?.map((paintRegion) => ({
        label: paintRegion.name,
        value: paintRegion.id,
      })) || [];
    return options;
  }, [paintRegionsData?.allPaintRegions]);

  const formattedDueDate = poi?.dueDate ? convertTimeStampToDate(poi?.dueDate) : undefined;

  const modifyCuboidDataInPoi = useCallback(
    (updatedData: AnalysisCuboid, targetId: string) => {
      setPoiFilteredByCheckbox((current) => {
        const copy = [...current];
        const updatedPoiIndex = copy.findIndex(({ id }) => id === targetId);
        if (updatedPoiIndex === -1) return current;

        const updatedPoi = {
          ...copy[updatedPoiIndex],
          cuboids: [updatedData],
        };

        copy[updatedPoiIndex] = updatedPoi;

        return copy;
      });
    },
    [setPoiFilteredByCheckbox]
  );

  const generalFieldsData = useGeneralFields({
    assignee: poi?.assignee || undefined,
    dueDate: formattedDueDate,
    name: poi?.name || undefined,
    poiId: poi?.poiId || undefined,
    status: (poi?.status as PoiStatus) || undefined,
    visibility: poi?.visibility || undefined,
    teamIds: poi?.teams?.map(({ id }) => id) || [],
    workpackNumber: poi?.workpackNumber || undefined,
    comment: poi?.comment || undefined,
    accessibility: poi?.accessibility || undefined,
  });
  const { data: teamsData, loading: teamsLoading } = useGetAllTeamsForCreateOrEditPoiQuery();

  const teamOptions = useMemo(() => {
    return teamsData?.allTeams?.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }, [teamsData?.allTeams]);

  const [mutate] = useUpdatePoiGeneralFieldsMutation({});

  const { showSnackBar } = useSnackBarMessage({});

  const { cancelEditing } = useTriggerPoiEditing();

  const { stopEditingPoiCuboids, doneEditingPoiCuboids } = useAnalysisCuboidActions();

  const isVoi = checkIfVoi(allVoiTemplates, poi?.type);

  const isWorkScope = checkByType(poi?.type || '');

  const locationId = useMemo(() => {
    if (!isVoi) return undefined;
    const candidateLocationId = currentSpherical?.id ?? editPointOfInterest?.poiSphericalId;
    const currentLocationId = poi?.location?.id;
    if (!candidateLocationId) return undefined;
    if (candidateLocationId === currentLocationId) return undefined;
    return candidateLocationId;
  }, [
    isVoi,
    currentSpherical?.id,
    editPointOfInterest?.poiSphericalId,
    poi?.location?.id,
  ]);

  // Update selectedPaintRegionId when POI or paintRegion data changes
  useEffect(() => {
    // If we have a paint region on the POI, use it
    if (poi?.paintRegion?.name && isWorkScope && isAtViewerPage) {
      // Find matching paint region by name since we might not have the ID
      const matchingRegion = paintRegionsData?.allPaintRegions?.find(
        (r) => r.name === poi.paintRegion?.name
      );

      if (matchingRegion) {
        setSelectedPaintRegionId(matchingRegion.id);
      } else if (poi.paintRegion.id) {
        // Fallback to using ID directly if name doesn't match but we have an ID
        setSelectedPaintRegionId(poi.paintRegion.id);
      }
    } else if (paintRegionsData?.allPaintRegions?.length) {
      setSelectedPaintRegionId('');
    }
  }, [
    poi?.paintRegion,
    paintRegionsData,
    paintRegionsLoading,
    paintRegionsError,
    isWorkScope,
    isAtViewerPage,
  ]);

  const shouldIncludeCuboids = useMemo(() => {
    if (!currentCuboid) return false;
    return isWorkScope || isVoi;
  }, [isVoi, isWorkScope, currentCuboid]);

  // Lazy query for finding closest paint region
  const [findClosestPaintRegion] = useFindClosestPaintRegionLazyQuery();

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLElement>) => {
      event.preventDefault();
      setLoading(true);

      // Use the parent paint region selected in the dropdown, if available
      // Only find closest paint region if no manual selection and cuboid exists
      let parentPaintRegionId = undefined;

      // If cuboid exists and no manual paint region selected, find closest paint region
      if (currentCuboid?.position && structureId) {
        try {
          const { data } = await findClosestPaintRegion({
            variables: {
              input: {
                position: {
                  x: currentCuboid.position[0],
                  y: currentCuboid.position[1],
                  z: currentCuboid.position[2],
                },
                structureId: structureId,
              },
            },
          });

          if (data?.findClosestPaintRegion?.paintRegion?.id) {
            parentPaintRegionId = data.findClosestPaintRegion.paintRegion.id;
          }
        } catch (error) {
          console.error('Error finding closest paint region:', error);
          // Continue with submission even if query fails
        }
      }
      const stringifiedDueDate = generalFieldsData.state.dueDate
        ? String(generalFieldsData.state.dueDate)
        : undefined;

      const accessibilityEnum = Object.values(PoiAccessibilityEnum).find(
        (accessibilityValue) =>
          accessibilityValue === upperSnakeCase(generalFieldsData.state.accessibility || '')
      );
      // Do not pass poiId for update mutation since this id is not user defined
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { poiId, ...generalFieldsState } = generalFieldsData.state;
      // Include paint region in the update if it has changed - same as in CreatePoi
      const paintRegionInput =
        selectedPaintRegionId && selectedPaintRegionId !== poi?.paintRegion?.id
          ? { paintRegionId: selectedPaintRegionId }
          : {};

      mutate({
        variables: {
          input: {
            pointOfInterestId: poi?.id || '',
            ...generalFieldsState,
            ...paintRegionInput,
            ...(parentPaintRegionId ? { parentPaintRegionId } : {}),
            ...(locationId ? { locationId } : {}),
            dueDate: stringifiedDueDate || undefined,
            ...(accessibilityEnum ? { accessibility: accessibilityEnum } : {}),
            // Reason to update templateFeildData, even when updating general fields is
            // that when updating the location of a VOI, we fetch 'defects' for that VOI
            // and these defects have to go into templateFieldData, but after the VOI cuboid
            // measurement is done, we move to the general editing screen. So those updated defects
            // are never saved
            templateFieldData: poi?.templateFieldData,
            cuboids: shouldIncludeCuboids
              ? [
                  {
                    position: currentCuboid?.position,
                    scale: currentCuboid?.scale,
                    rotation: currentCuboid?.rotation,
                    // currentCuboid is already check for undefined
                    // in `shouldIncludeCuboids`
                  } as CuboidInput,
                ]
              : undefined,
            lastModifiedFor: 'Update General Fields',
          },
        },
        refetchQueries: [
          SelectedPointOfInterestDocument,
          AllPointOfInterestsForDataLoaderDocument,
          PointOfInterestsCountByStatusDocument,
          PointOfInterestsCountByInspectionPriorityDocument,
          PointOfInterestsCountByDueDateDocument,
          PaintDamageCountByAlarmLevelDocument,
        ],
      })
        .then((response) => {
          const newCuboids = response?.data?.updatePointOfInterest?.cuboids;
          if (newCuboids) {
            modifyCuboidDataInPoi(newCuboids[0] as AnalysisCuboid, poi?.id || '');
          }
          showSnackBar(`${isWorkScope ? 'Work scope' : 'Point of interest'} updated`, 'success');
          cancelEditing();
          doneEditingPoiCuboids();
        })
        .catch(() => {
          showSnackBar(
            `Could not update ${isWorkScope ? 'work scope' : 'point of interest'}`,
            'error'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      generalFieldsData.state,
      mutate,
      poi?.id,
      currentCuboid,
      showSnackBar,
      isWorkScope,
      cancelEditing,
      doneEditingPoiCuboids,
      modifyCuboidDataInPoi,
      shouldIncludeCuboids,
      selectedPaintRegionId,
      poi?.paintRegion?.id,
      findClosestPaintRegion,
      structureId,
      poi?.templateFieldData,
      locationId,
    ]
  );

  return (
    <Box height="100%">
      <StyledForm onSubmit={handleSubmit}>
        <Stack
          sx={{
            height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`,
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box p={2} flex={1} mb="85px">
            <PoiFormSubHeading variant="h5">Edit General</PoiFormSubHeading>
            <GeneralFieldsForm
              teamsLoading={teamsLoading}
              teamOptions={teamOptions}
              dataHook={generalFieldsData}
              paintRegionOptions={paintRegionOptions}
              selectedPaintRegionId={selectedPaintRegionId}
              handlePaintRegionChange={handlePaintRegionChange}
            />
          </Box>
          <SaveCancelFormButtons
            cancelButtonLabel="Cancel"
            buttonLabel="Save"
            loadingLabel="Updating..."
            onCancel={() => {
              stopEditingPoiCuboids(editPointOfInterest?.pointOfInterest);
              cancelEditing();
            }}
            loading={loading}
            sx={saveCancelButtonCommonStyles.sx}
            saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
            cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
            saveButtonDisabled={isCuboidEditorEnabled}
          />
        </Stack>
      </StyledForm>
    </Box>
  );
};
